import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { InterviewStudent, InterviewRank } from "@/types";

interface StudentListProps {
  students?: InterviewStudent[];
}

// Calculate final rank
const calculateFinalRank = (ranks: InterviewRank): number => {
  const { html_css, javascript, react, backend } = ranks;
  return Number(((html_css + javascript + react + backend) / 4).toFixed(1));
};

const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case "completed":
      return "default";
    case "scheduled":
      return "secondary";
    case "pending":
      return "outline";
    default:
      return "outline";
  }
};

const StudentList: React.FC<StudentListProps> = ({ students }) => {
  // Show all students (not just scheduled) so instructor can see all grades
  const displayedStudents = React.useMemo(() => {
    return students || [];
  }, [students]);

  if (!displayedStudents || displayedStudents.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        No students found in this batch
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
              Student Name
            </th>
            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">
              Status
            </th>
            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">
              HTML/CSS
            </th>
            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">
              JavaScript
            </th>
            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">
              React
            </th>
            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">
              Backend
            </th>
            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">
              Avg Rank
            </th>
          </tr>
        </thead>
        <tbody>
          {displayedStudents.map((student) => {
            const finalRank = calculateFinalRank(student.ranks);
            const hasAnyRank = Object.values(student.ranks).some(
              (rank) => rank > 0,
            );

            return (
              <tr key={student._id} className="transition-colors border-b">
                <td className="px-4 py-3 text-sm font-medium text-gray-900  dark:text-gray-100 ">
                  {student.name || "Unknown"}
                </td>
                <td className="px-4 py-3 text-center">
                  <Badge variant={getStatusBadgeVariant(student.status)}>
                    {student.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-center text-sm text-blue-600 font-medium">
                  {student.ranks.html_css || (hasAnyRank ? "-" : "0")}
                </td>
                <td className="px-4 py-3 text-center text-sm text-yellow-600 font-medium">
                  {student.ranks.javascript || (hasAnyRank ? "-" : "0")}
                </td>
                <td className="px-4 py-3 text-center text-sm text-cyan-600 font-medium">
                  {student.ranks.react}
                </td>
                <td className="px-4 py-3 text-center text-sm text-red-600 font-medium">
                  {student.ranks.backend || (hasAnyRank ? "-" : "0")}
                </td>
                <td className="px-4 py-3 text-center text-sm font-bold text-purple-600">
                  {hasAnyRank ? finalRank.toFixed(1) : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default StudentList;
